/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from 'react';

// @mui
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, AlertTitle, Box, Button, Divider, Tab, Tabs, TextField, Typography, styled } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import dynamic from "next/dynamic";
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useStopwatch } from 'react-timer-hook';
import * as Yup from 'yup';
import Iconify from '../../components/Iconify';
import Image from '../../components/Image';
import Scrollbar from '../../components/Scrollbar';
import { FormProvider } from '../../components/hook-form';
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
import { checkCodeAPI, submitCodeAPI } from '../../service/hackerthon/hackerthon.code.service';
import { fNumber } from '../../utils/formatNumber';
import { LANGUAGES, THEMES } from '../../utils/languages';
const MonacoEditor = dynamic(import("@monaco-editor/react"), { ssr: false });

const TabPanelStyle = styled(Stack)(({ theme }) => ({
  height: `calc(100vh - 95px)`,
  display: 'flex',
  [theme.breakpoints.down('xs')]: {
    height: '100vh'
  }
}));

export default function ExerciseIDE() {
  const { translate } = useLocales();
  const { exercise } = useSelector((state) => state.hackerthonExercise);
  const [editorHeight, setEditorHeight] = useState(200);
  const [showConsole, setShowConsole] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated } = useAuth();

  const NewItemSchema = Yup.object().shape({
    language: Yup.string().required(translate('validation.required')),
    sourcecode: Yup.string().required(translate('validation.required')),
  });

  const defaultValues = useMemo(
    () => ({
      language: LANGUAGES[0].name,
      sourcecode: LANGUAGES[0].code,
      theme: THEMES[0],
    }), []
  );

  const methods = useForm({
    resolver: yupResolver(NewItemSchema),
    defaultValues,
  });

  const { watch, control, setValue, getValues, handleSubmit, formState: { isSubmitting } } = methods;

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'language') {
        const data = LANGUAGES.find(item => item.name === value.language);
        setValue("sourcecode", data.code, { shouldValidate: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const [response, setResponse] = useState(null);

  const onRun = async () => {
    if (!isAuthenticated) {
      enqueueSnackbar(translate("label.signinToJmaster"), { variant: 'error' });
      return;
    }

    const request = {
      language: getValues("language"),
      sourcecode: getValues("sourcecode"),
      testCases: exercise?.testCases || []
    };

    const { code, data, message } = await checkCodeAPI(request);
    if (code === '200') {
      setResponse(data);
      !showConsole && handleShowConsole(); //show console
    } else
      if (code === '403')
        enqueueSnackbar(`${translate("label.signinToJmaster")}`, { variant: 'error' });
      else
        enqueueSnackbar(`${code} : ${message}`, { variant: 'error' });
  };

  const onSubmit = async (request) => {
    if (!isAuthenticated) {
      enqueueSnackbar(translate("label.signinToJmaster"), { variant: 'error' });
      return;
    }
    //add exercise id
    request.exerciseId = exercise?.id;

    const { code, data, message } = await submitCodeAPI(request);
    if (code === '200') {
      setResponse(data);
      !showConsole && handleShowConsole(); //show console
    } else
      if (code === '403')
        enqueueSnackbar(`${translate("label.signinToJmaster")}`, { variant: 'error' });
      else
        enqueueSnackbar(`${code} : ${message}`, { variant: 'error' });
  };

  const handleShowConsole = () => {
    const show = !showConsole;
    setShowConsole(show);
    setEditorHeight(show ? 550 : 200);
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <TabPanelStyle spacing={1} p={1}>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
          <Stack direction={"row"} spacing={1}>
            <Controller
              name="language"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl>
                  <InputLabel>{translate('ide.language')}</InputLabel>
                  <Select size='small' label={translate('ide.language')} {...field} error={!!error} sx={{ width: 150 }}>
                    {LANGUAGES.map((item, index) => (
                      <MenuItem key={index} value={item.name}>
                        <Stack direction={'row'} spacing={2} alignItems={'center'}>
                          <Image src={item.image} sx={{ height: 15, width: 15 }} alt={item.title} />
                          <Typography variant="subtitle2">{item.title}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="theme"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl>
                  <InputLabel>{translate('ide.theme')}</InputLabel>
                  <Select size='small' label={translate('ide.theme')} {...field} error={!!error} sx={{ width: 100 }}>
                    {THEMES.map((item, index) => (
                      <MenuItem key={index} value={item}>
                        <Typography variant="subtitle2">{item}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
          <Stopwatcher />
        </Stack>
        <Controller
          name="sourcecode"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <MonacoEditor {...field} fullWidth error={!!error} options={{ minimap: { enabled: false } }}
              width="100wh" height={`calc(100vh - ${editorHeight}px)`} language={watch("language").toLowerCase() === 'cs' ? 'csharp' : watch("language").toLowerCase()} theme={watch("theme")}
            />
          )}
        />
        {showConsole && <Scrollbar>
          <Console response={response} testCases={exercise?.testCases} />
        </Scrollbar>}
        <Divider />
        <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
          <Box>
            <Button type="button" color='inherit' variant="outlined"
              startIcon={<Iconify icon={'clarity:play-solid'} sx={{ height: 20, width: 20 }} />}
              onClick={onRun}>
              {translate('button.run')}
            </Button>
            <Button type="button" color='inherit' variant="text" onClick={handleShowConsole}
              startIcon={<Iconify icon={showConsole ? 'solar:double-alt-arrow-down-line-duotone' : 'solar:double-alt-arrow-up-line-duotone'} sx={{ height: 20, width: 20 }} />}>
              Console
            </Button>
          </Box>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}
            startIcon={<Iconify icon={'tabler:send'} sx={{ height: 20, width: 20 }} />}>
            {translate('button.submit')}
          </LoadingButton>
        </Stack>
      </TabPanelStyle>
    </FormProvider >
  );
}

function Console({ response, testCases = [] }) {
  const [tab, setTab] = useState(0);
  const { translate } = useLocales();

  if (response) {
    const { statusCode, verdict, error, testCasesResults } = response;


    const renderTestCase = () => (
      testCasesResults?.length > 0 && <>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant='scrollable' allowScrollButtonsMobile>
          {testCasesResults?.map((testCaseResult, index) => (
            <Tab key={index} value={index} label={`Test #${index}`}
              icon={<Iconify icon={"radix-icons:dot-filled"}
                sx={{ color: testCaseResult.verdictStatusCode === 100 ? 'green' : 'red' }} />} />
          ))}
        </Tabs>
        <TextField label={translate("hackerthon.testCaseResult.input")} value={testCasesResults[tab]?.input} disabled multiline />
        <TextField label={translate("hackerthon.testCaseResult.expectedOutput")} value={testCasesResults[tab]?.expectedOutput} disabled multiline />
        <TextField label={translate("hackerthon.testCaseResult.output")} value={testCasesResults[tab]?.output} disabled multiline />
      </>
    );

    if (statusCode !== 100)
      return (
        <Stack spacing={2} my={1}>
          <Alert severity="error">
            <AlertTitle>{statusCode} : {verdict}</AlertTitle>
            {error}
          </Alert>
          {renderTestCase()}
        </Stack>
      );
    else
      return (<Stack spacing={2} my={1}>
        <Alert severity="success">
          <AlertTitle>{verdict} ({statusCode})</AlertTitle>
          Runtime: {fNumber(response.averageExecutionDuration)} ms
        </Alert>
        {renderTestCase()}
      </Stack>);
  } if (testCases?.length > 0)
    return <Stack spacing={2} my={1}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} >
        {testCases?.map((_, index) => (
          <Tab key={index} value={index} label={`Test #${index}`} />
        ))}
      </Tabs>
      <TextField label={translate("hackerthon.testCaseResult.input")} value={testCases[tab]?.input} multiline disabled />
      <TextField label={translate("hackerthon.testCaseResult.expectedOutput")} value={testCases[tab]?.expectedOutput} multiline disabled />
    </Stack>;
  else
    return <Typography variant='subtitle2'>Console:</Typography>;
}

function Stopwatcher() {
  const {
    seconds,
    minutes,
    hours,
    reset,
  } = useStopwatch({ autoStart: true });

  return (
    <Stack direction={"row"} spacing={1} alignItems={"center"}>
      <Typography variant='subtitle2' >
        {hours}:{minutes}:{seconds}
      </Typography>
      <Iconify icon={"system-uicons:reset-alt"} sx={{ height: 12, width: 12, cursor: 'pointer' }} onClick={reset} />
    </Stack>
  );
}
